"use client";
import React, { useState } from "react";
import { Shield, CheckCircle2, XCircle } from "lucide-react";

export const InteractiveMiddleware = () => {
    const [middlewareName, setMiddlewareName] = useState("AuthMiddleware");
    const [middlewareType, setMiddlewareType] = useState("authentication");
    const [enabled, setEnabled] = useState(true);
    const [order, setOrder] = useState(1);

    const types = [
        { value: "authentication", label: "Authentication", color: "text-blue-500" },
        { value: "authorization", label: "Authorization (RBAC)", color: "text-purple-500" },
        { value: "validation", label: "Validation", color: "text-green-500" },
        { value: "rate_limiting", label: "Rate Limiting", color: "text-amber-500" },
        { value: "logging", label: "Logging", color: "text-gray-500" },
        { value: "custom", label: "Custom", color: "text-pink-500" }
    ];

    const successResponses = [
        { code: 200, message: "Request processed successfully" },
        { code: 201, message: "Resource created" }
    ];

    const failureResponses = [
        { code: 401, message: "Unauthorized - Invalid credentials" },
        { code: 403, message: "Forbidden - Insufficient permissions" },
        { code: 429, message: "Too many requests" }
    ];

    const selectedType = types.find(t => t.value === middlewareType);

    return (
        <div className="w-full max-w-2xl mx-auto bg-surface border border-border rounded-xl overflow-hidden shadow-2xl">
            {/* Header */}
            <div className="bg-background/50 backdrop-blur-sm p-4 border-b border-border flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <div className="p-1.5 bg-primary/10 rounded-lg">
                        <Shield className="w-4 h-4 text-primary" />
                    </div>
                    <span className="font-semibold text-primary-text text-sm">Middleware Builder</span>
                </div>
                <div className="flex gap-1.5">
                    <div className="w-2.5 h-2.5 rounded-full bg-red-500/20 border border-red-500/50" />
                    <div className="w-2.5 h-2.5 rounded-full bg-yellow-500/20 border border-yellow-500/50" />
                    <div className="w-2.5 h-2.5 rounded-full bg-green-500/20 border border-green-500/50" />
                </div>
            </div>

            {/* Content */}
            <div className="p-6 space-y-6 bg-gradient-to-b from-surface to-background">
                {/* Middleware Name */}
                <div className="space-y-2">
                    <label className="text-xs font-medium text-secondary-text uppercase tracking-wider">
                        Middleware Name
                    </label>
                    <input
                        type="text"
                        value={middlewareName}
                        onChange={(e) => setMiddlewareName(e.target.value)}
                        className="w-full bg-background border border-border rounded-lg p-3 text-sm text-primary-text font-mono focus:outline-none focus:ring-2 focus:ring-primary/50 transition-all"
                    />
                </div>

                {/* Middleware Type */}
                <div className="space-y-2">
                    <label className="text-xs font-medium text-secondary-text uppercase tracking-wider">
                        Middleware Type
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                        {types.map((type) => (
                            <button
                                key={type.value}
                                onClick={() => setMiddlewareType(type.value)}
                                className={`p-3 rounded-lg border text-left transition-all ${middlewareType === type.value
                                        ? "bg-primary/10 border-primary"
                                        : "bg-background border-border hover:border-primary/30"
                                    }`}
                            >
                                <div className={`text-sm font-semibold ${middlewareType === type.value ? "text-primary" : type.color
                                    }`}>
                                    {type.label}
                                </div>
                            </button>
                        ))}
                    </div>
                </div>

                {/* Enabled Toggle & Order */}
                <div className="grid grid-cols-2 gap-4">
                    <div className="flex items-center justify-between p-4 bg-background/50 rounded-lg border border-border">
                        <div>
                            <div className="text-sm font-semibold text-primary-text">Enabled</div>
                            <div className="text-xs text-secondary-text">
                                {enabled ? "Active" : "Disabled"}
                            </div>
                        </div>
                        <button
                            onClick={() => setEnabled(!enabled)}
                            className={`w-11 h-6 rounded-full relative transition-colors ${enabled ? "bg-primary" : "bg-border"
                                }`}
                        >
                            <div
                                className={`w-4 h-4 bg-white rounded-full absolute top-1 transition-transform ${enabled ? "left-6" : "left-1"
                                    }`}
                            />
                        </button>
                    </div>

                    <div className="space-y-2">
                        <label className="text-xs font-medium text-secondary-text uppercase tracking-wider">
                            Execution Order
                        </label>
                        <input
                            type="number"
                            value={order}
                            onChange={(e) => setOrder(parseInt(e.target.value) || 1)}
                            min="1"
                            className="w-full bg-background border border-border rounded-lg p-3 text-sm text-primary-text focus:outline-none focus:ring-2 focus:ring-primary/50 transition-all"
                        />
                    </div>
                </div>

                {/* Success Responses */}
                <div className="space-y-2">
                    <label className="text-xs font-medium text-secondary-text uppercase tracking-wider">
                        Success Responses
                    </label>
                    <div className="space-y-2">
                        {successResponses.map((response, index) => (
                            <div
                                key={index}
                                className="flex items-start gap-3 p-3 bg-green-500/5 border border-green-500/20 rounded-lg"
                            >
                                <CheckCircle2 className="w-5 h-5 text-green-500 shrink-0 mt-0.5" />
                                <div className="flex-1">
                                    <div className="text-sm font-semibold text-primary-text">
                                        {response.code}
                                    </div>
                                    <div className="text-xs text-secondary-text">
                                        {response.message}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Failure Responses */}
                <div className="space-y-2">
                    <label className="text-xs font-medium text-secondary-text uppercase tracking-wider">
                        Failure Responses
                    </label>
                    <div className="space-y-2">
                        {failureResponses.map((response, index) => (
                            <div
                                key={index}
                                className="flex items-start gap-3 p-3 bg-red-500/5 border border-red-500/20 rounded-lg"
                            >
                                <XCircle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
                                <div className="flex-1">
                                    <div className="text-sm font-semibold text-primary-text">
                                        {response.code}
                                    </div>
                                    <div className="text-xs text-secondary-text">
                                        {response.message}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Preview */}
                <div className="p-4 bg-background/80 rounded-lg border border-border/50">
                    <div className="text-xs font-medium text-secondary-text uppercase tracking-wider mb-2">
                        Middleware Configuration
                    </div>
                    <div className="font-mono text-xs space-y-1 text-primary-text">
                        <div>Name: <span className="text-primary">{middlewareName}</span></div>
                        <div>Type: <span className={selectedType?.color}>{selectedType?.label}</span></div>
                        <div>Order: <span className="text-amber-400">#{order}</span></div>
                        <div>Status: <span className={enabled ? "text-green-500" : "text-red-500"}>
                            {enabled ? "Enabled" : "Disabled"}
                        </span></div>
                    </div>
                </div>
            </div>
        </div>
    );
};
